import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Trash2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGE_GROUPS = ['Junior League', 'Champions League', 'World Cup', 'Icons League'];
const GROUPED_LEAGUES = ['Champions League', 'Icons League'];

export default function ManageTeams() {
  const [teams, setTeams] = useState([]);
  const [filterAge, setFilterAge] = useState('all');
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState('Junior League');
  const [group, setGroup] = useState('A');
  const [logoUrl, setLogoUrl] = useState('');

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeams(data);
    } catch (error) {
      toast.error('Failed to load teams');
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/teams`,
        {
          name,
          age_group: ageGroup,
          group: GROUPED_LEAGUES.includes(ageGroup) ? group : null,
          logo_url: logoUrl || null,
        },
        { withCredentials: true }
      );
      toast.success('Team created');
      setName('');
      setLogoUrl('');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Delete this team?')) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`, { withCredentials: true });
      toast.success('Team deleted');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const filteredTeams = filterAge === 'all' ? teams : teams.filter(t => t.age_group === filterAge);

  return (
    <div className="space-y-6">
      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#D4AF37]" />
          Add New Team
        </h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-white mb-2">Team Name</Label>
            <Input
              data-testid="team-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-[#0A0F1C] border-white/20 text-white"
              placeholder="Lions FC"
            />
          </div>
          <div>
            <Label className="text-white mb-2">League</Label>
            <Select value={ageGroup} onValueChange={setAgeGroup}>
              <SelectTrigger data-testid="team-age-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGE_GROUPS.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {GROUPED_LEAGUES.includes(ageGroup) && (
            <div>
              <Label className="text-white mb-2">Group</Label>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger data-testid="team-group-select" className="bg-[#0A0F1C] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">Group A</SelectItem>
                  <SelectItem value="B">Group B</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-white mb-2">Logo URL (optional)</Label>
            <Input
              data-testid="team-logo-input"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              className="bg-[#0A0F1C] border-white/20 text-white"
              placeholder="https://..."
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              data-testid="create-team-button"
              className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
            >
              Add Team
            </Button>
          </div>
        </form>
      </Card>

      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">All Teams ({filteredTeams.length})</h3>
          <Select value={filterAge} onValueChange={setFilterAge}>
            <SelectTrigger data-testid="filter-teams-age" className="w-40 bg-[#0A0F1C] border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {AGE_GROUPS.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeams.map((team) => (
            <div
              key={team.id}
              data-testid={`team-card-${team.id}`}
              className="bg-[#0A0F1C] border border-white/5 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {team.logo_url ? (
                  <img src={team.logo_url} alt={team.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#0038B8] flex items-center justify-center text-white font-bold">
                    {team.name.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="text-white font-semibold">{team.name}</p>
                  <p className="text-[#9BA3AF] text-sm">
                    {team.age_group}
                    {team.group && <span className="ml-2 text-[#D4AF37]">Group {team.group}</span>}
                  </p>
                </div>
              </div>
              <Button
                data-testid={`delete-team-${team.id}`}
                onClick={() => handleDelete(team.id)}
                size="sm"
                className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
